import * as React from 'react';
import { gql, useMutation } from '@apollo/client';
import {
  SegmentOperator,
  CanonicalEventName,
  CountOperator,
  CreateSegmentInput,
  ConditionTypeEnum,
  TimeBoundaryType
} from '@/gql/graphql';
import { GET_ALL_SEGMENTS_METADATA } from '@/queries/segments';
import type { Activity } from '../components/dialog-steps/SelectActivityStep';
import type { BuildScratchFormValues } from '../components/dialog-steps/BuildScratchStep';

// --- State Structure Definition --- //
interface ConditionStateItem {
  clientId: string;
  displayName: string;
  type: 'event';
  eventName: CanonicalEventName;
}

// --- Step Definition --- //
export type SegmentCreationStep =
  | 'initial'
  | 'buildScratch'
  | 'selectConditionType'
  | 'selectActivity';

// --- GraphQL Mutation Definition --- //
const CREATE_SEGMENT_MUTATION = gql`
  mutation CreateSegment($input: CreateSegmentInput!) {
    createSegment(input: $input)
  }
`;

// --- The Custom Hook --- //
interface UseCreateSegmentDialogOptions {
  onSegmentCreated: (segmentId: string) => void;
}

export function useCreateSegmentDialog(isOpen: boolean, options: UseCreateSegmentDialogOptions) {
  const { onSegmentCreated } = options;
  // Keep state for dialog steps, name, description
  const [step, setStep] = React.useState<SegmentCreationStep>('initial');
  const [segmentName, setSegmentName] = React.useState('');
  const [segmentDescription, setSegmentDescription] = React.useState('');
  // Keep internal state for collected conditions
  const [conditions, setConditions] = React.useState<ConditionStateItem[]>([]);
  const defaultGroupOperator = SegmentOperator.And;

  const [createSegmentMutation, { loading: isCreatingSegment, error: createError }] = useMutation<
    { createSegment: string },
    { input: CreateSegmentInput }
  >(CREATE_SEGMENT_MUTATION, {
    refetchQueries: [{ query: GET_ALL_SEGMENTS_METADATA, variables: { includeDeleted: false } }]
  });

  React.useEffect(() => {
    if (isOpen) {
      setStep('initial');
      setSegmentName('');
      setSegmentDescription('');
      setConditions([]);
    }
  }, [isOpen]);

  const handleBuildScratch = React.useCallback(() => {
    setStep('buildScratch');
  }, []);
  const handleUseTemplate = React.useCallback(() => {
    console.log('Use template selected - TBD');
  }, []);
  const handleUploadCsv = React.useCallback(() => {
    console.log('Upload CSV selected - TBD');
  }, []);
  const handleAddCondition = React.useCallback(() => {
    setStep('selectConditionType');
  }, []);
  const handleSelectActivity = React.useCallback(() => {
    setStep('selectActivity');
  }, []);
  const handleSelectCharacteristic = React.useCallback(() => {
    console.log('Select Characteristic - TBD');
  }, []);

  const handleDeleteCondition = React.useCallback((clientId: string) => {
    setConditions((prev) => prev.filter((condition) => condition.clientId !== clientId));
  }, []);

  const handleAddSelectedConditions = React.useCallback((selectedActivities: Activity[]) => {
    const newConditions: ConditionStateItem[] = selectedActivities.map((activity) => ({
      clientId: crypto.randomUUID(),
      type: 'event',
      displayName: activity.name,
      eventName: activity.id
    }));
    setConditions((prev) => [...prev, ...newConditions]);
    setStep('buildScratch');
  }, []);

  const handleGoBack = React.useCallback(() => {
    setStep((prevStep) => {
      switch (prevStep) {
        case 'selectActivity':
          return 'selectConditionType';
        case 'selectConditionType':
          return 'buildScratch';
        case 'buildScratch':
          return 'initial';
        default:
          return prevStep;
      }
    });
  }, []);

  const handleCreateSegment = React.useCallback(
    async (formData: BuildScratchFormValues) => {
      console.log('Constructing CreateSegmentInput...');

      // Map internal conditions state to the required ConditionGroupInput structure
      const conditionGroupConditions = conditions.map((cond) => {
        // Construct the condition object using imported Enum members
        return {
          conditionType: ConditionTypeEnum.Event, // Use imported Enum
          eventName: cond.eventName, // Use value from state (should match Enum)
          operator: CountOperator.GreaterThanOrEqualTo, // Use imported Enum (Example default)
          numTimes: 1, // Default
          timeBoundary: {
            // Default
            type: TimeBoundaryType.AnyTime // Use imported Enum
          }
          // eventPropertyFilters: [],
        }; // No cast needed if structure matches expected input condition object
      });

      // Construct the main mutation input matching CreateSegmentInput GQL type
      const mutationInput: CreateSegmentInput = {
        name: formData.segmentName,
        description: formData.segmentDescription ?? '',
        rootOperator: SegmentOperator.And, // Use imported Enum
        groups: [
          {
            operator: defaultGroupOperator, // Use imported Enum
            conditions: conditionGroupConditions
          }
        ]
      };

      console.log('Calling createSegment mutation with variables:', { input: mutationInput });
      try {
        const response = await createSegmentMutation({ variables: { input: mutationInput } });
        const newSegmentId = response.data?.createSegment;

        if (newSegmentId) {
          onSegmentCreated(newSegmentId);
        } else {
          console.error('Mutation succeeded but no segment ID was returned.');
        }
      } catch (err) {
        console.error('Mutation failed:', err);
      }
    },
    [conditions, defaultGroupOperator, createSegmentMutation, onSegmentCreated]
  );

  // --- Return Value --- //
  return {
    step,
    segmentName,
    segmentDescription,
    setSegmentName,
    setSegmentDescription,
    conditions,
    handlers: {
      handleBuildScratch,
      handleUseTemplate,
      handleUploadCsv,
      handleAddCondition,
      handleDeleteCondition,
      handleSelectActivity,
      handleSelectCharacteristic,
      handleAddSelectedConditions,
      handleGoBack
    },
    createSegmentHandler: handleCreateSegment,
    isCreatingSegment,
    createError
  };
}
